import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button, Card, Input, Checkbox, Alert, Icon } from '@/components/ui';
import { en } from '@/i18n/en';
import { useAuth } from '@/state/AuthContext';
import { forgotPassword } from '@/lib/auth';
import { passwordStrength } from '@/lib/format';

const SAFE_NEXT = /^\/dashboard(\/.*)?$/;

const signinSchema = z.object({
  email: z.string().email('Veuillez entrer une adresse email valide'),
  password: z.string().min(1, 'Le mot de passe est requis'),
});

const signupSchema = z.object({
  name: z.string().min(2, 'Veuillez entrer votre nom'),
  email: z.string().email('Veuillez entrer une adresse email valide'),
  password: z.string()
    .min(10, 'Au moins 10 caractères')
    .regex(/[A-Z]/, 'Ajoutez une lettre majuscule')
    .regex(/[a-z]/, 'Ajoutez une lettre minuscule')
    .regex(/\d/, 'Ajoutez un chiffre')
    .regex(/[^A-Za-z0-9]/, 'Ajoutez un symbole spécial'),
  confirm: z.string(),
  acceptTerms: z.literal(true, { errorMap: () => ({ message: en.auth.errors.mustAcceptTerms }) }),
}).refine((v) => v.password === v.confirm, { message: en.auth.errors.mismatch, path: ['confirm'] });

type SigninForm = z.infer<typeof signinSchema>;
type SignupForm = z.infer<typeof signupSchema>;

type Tab = 'signin' | 'signup';

export function AuthPage() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') === 'signup' ? 'signup' : 'signin';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [serverError, setServerError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { signIn, signUp, status } = useAuth();
  const nav = useNavigate();

  const nextRaw = params.get('next') ?? '/dashboard';
  const next = SAFE_NEXT.test(nextRaw) ? nextRaw : '/dashboard';

  useEffect(() => {
    if (status === 'authenticated') nav(next, { replace: true });
  }, [status, next, nav]);

  function switchTab(t: Tab) {
    setTab(t);
    setServerError(null);
    setParams((p) => { p.set('tab', t); return p; }, { replace: true });
  }

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <aside className="auth-showcase">
          <Link to="/" className="auth-brand"><span className="auth-brand-mark">M</span><strong>Ma Facture</strong><span className="auth-brand-divider"/><small>ESPACE DE GESTION</small></Link>
          <div className="auth-showcase-copy"><span className="auth-eyebrow"><i/> VOTRE ESPACE, ENFIN CLAIR</span><h1>La paperasse<br/>prend <em>moins de place.</em></h1><p>Retrouvez vos factures, vos reçus et vos informations au même endroit.</p></div>
          <div className="auth-art" aria-hidden="true">
            <div className="auth-art-sun"/><div className="auth-art-orbit auth-art-orbit-a"/><div className="auth-art-orbit auth-art-orbit-b"/>
            <div className="auth-invoice-sheet"><div className="auth-sheet-head"><span className="auth-sheet-logo">M</span><span className="auth-sheet-tag">FACTURE</span></div><strong className="auth-sheet-title">Votre document<br/>en un coup d’œil.</strong><span className="auth-sheet-rule"/><span className="auth-sheet-line short"/><span className="auth-sheet-line"/><span className="auth-sheet-line medium"/><div className="auth-sheet-total"><span>Total TTC</span><strong>1 250,00 <small>MAD</small></strong></div></div>
            <div className="auth-art-chip"><span><Icon name="check" size={15}/></span><div><strong>Tout est bien rangé</strong><small>Vos documents, au même endroit</small></div></div><span className="auth-art-star">✳</span>
          </div>
          <div className="auth-showcase-foot"><span>Simple à utiliser</span><i/><span>Conçu pour le Maroc</span><i/><span>En MAD</span></div>
        </aside>

        <Card className={`auth-card ${tab === 'signup' ? 'is-signup' : 'is-signin'}`}>
          <Link to="/" className="auth-mobile-brand"><span className="auth-brand-mark">M</span><strong>Ma Facture</strong></Link>
          <div className="auth-form-heading"><span className="auth-form-kicker">{tab === 'signin' ? 'CONTENT DE VOUS REVOIR' : 'BIENVENUE CHEZ MA FACTURE'}</span><h2>{tab === 'signin' ? 'Ravi de vous revoir.' : 'Créez votre espace.'}</h2><p>{tab === 'signin' ? 'Connectez-vous pour retrouver vos documents.' : 'Quelques informations et votre espace est prêt.'}</p></div>
          <div className="auth-tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'signin'} className={tab === 'signin' ? 'active' : ''} onClick={() => switchTab('signin')}>{en.auth.signin}</button>
            <button role="tab" aria-selected={tab === 'signup'} className={tab === 'signup' ? 'active' : ''} onClick={() => switchTab('signup')}>{en.auth.signup}</button>
          </div>
          {serverError && <Alert tone="danger">{serverError}</Alert>}
          {tab === 'signin'
            ? <SignInForm onError={setServerError} onSubmit={signIn} onForgot={() => setForgotOpen(true)} />
            : <SignUpForm onError={setServerError} onSubmit={signUp} />}
          <div className="auth-form-foot"><Icon name="lock" size={14}/><span>Vos informations restent protégées.</span></div>
          {forgotOpen && <ForgotPanel onClose={() => setForgotOpen(false)} />}
        </Card>
      </div>
    </div>
  );
}

function SignInForm({ onSubmit, onError, onForgot }: {
  onSubmit: (input: SigninForm) => Promise<void>;
  onError: (e: string | null) => void;
  onForgot: () => void;
}) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<SigninForm>({
    resolver: async (values) => {
      const r = signinSchema.safeParse(values);
      if (r.success) return { values: r.data, errors: {} };
      const errs = Object.fromEntries(
        r.error.issues.map((i) => [i.path[0], { type: 'validation', message: i.message }]),
      );
      return { values: {}, errors: errs };
    },
  });

  async function submit(v: SigninForm) {
    onError(null);
    try { await onSubmit(v); }
    catch (e) {
      const msg = e instanceof Error && e.message ? e.message : en.auth.errors.generic;
      onError(en.auth.errors.generic === msg ? msg : en.auth.errors.generic);
    }
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="stack" noValidate>
      <Input
        label={en.auth.emailLabel}
        type="email"
        autoComplete="email"
        placeholder={en.auth.emailPlaceholder}
        error={errors.email?.message}
        {...register('email')}
      />
      <Input
        label={en.auth.passwordLabel}
        type="password"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register('password')}
      />
      <div className="row-between" style={{ marginTop: 8 }}>
        <button type="button" className="lnk lnk-ghost" onClick={onForgot}>{en.auth.forgotPassword}</button>
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? '…' : en.auth.submit.signin}
        </Button>
      </div>
    </form>
  );
}

function SignUpForm({ onSubmit, onError }: {
  onSubmit: (input: SignupForm) => Promise<void>;
  onError: (e: string | null) => void;
  onForgot?: () => void;
}) {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<SignupForm>({
    defaultValues: { acceptTerms: false as unknown as true },
    resolver: async (values) => {
      const r = signupSchema.safeParse(values);
      if (r.success) return { values: r.data, errors: {} };
      const errs = Object.fromEntries(
        r.error.issues.map((i) => [i.path[0], { type: 'validation', message: i.message }]),
      );
      return { values: {}, errors: errs };
    },
  });

  const pw = watch('password') || '';
  const strength = useMemo(() => passwordStrength(pw), [pw]);
  const strengthColor = ['#BFBDB9', '#9C9A95', '#7A7773', '#3F3D3A', '#1B1B1A'][strength.score];
  const strengthWidth = `${(strength.score / 4) * 100}%`;

  async function submit(v: SignupForm) {
    onError(null);
    try { await onSubmit(v); }
    catch (e) {
      // Use a single generic message regardless of duplicate-email or other failures.
      const msg = e instanceof Error ? e.message : '';
      onError(/email/i.test(msg) ? en.auth.errors.generic : (msg || en.auth.errors.generic));
    }
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="stack" noValidate>
      <Input
        label={en.auth.nameLabel}
        autoComplete="name"
        placeholder={en.auth.namePlaceholder}
        error={errors.name?.message}
        {...register('name')}
      />
      <Input
        label={en.auth.emailLabel}
        type="email"
        autoComplete="email"
        placeholder={en.auth.emailPlaceholder}
        error={errors.email?.message}
        {...register('email')}
      />
      <div>
        <Input
          label={en.auth.passwordLabel}
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password')}
        />
        <div className="pw-strength" aria-hidden>
          <div style={{ width: strengthWidth, background: strengthColor }} />
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>Force du mot de passe : {strength.label}</div>
      </div>
      <Input
        label={en.auth.confirmLabel}
        type="password"
        autoComplete="new-password"
        error={errors.confirm?.message}
        {...register('confirm')}
      />
      <Checkbox
        label={
          <span>
            J'accepte les <Link to="/terms">Conditions Générales</Link> et la <Link to="/privacy">Politique de Confidentialité</Link>.
          </span>
        }
        {...register('acceptTerms')}
      />
      {errors.acceptTerms && <span className="err" role="alert">{errors.acceptTerms.message}</span>}
      <Button type="submit" variant="primary" disabled={isSubmitting}>
        {isSubmitting ? '…' : en.auth.submit.signup}
      </Button>
    </form>
  );
}

function ForgotPanel({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function send() {
    setBusy(true);
    try { await forgotPassword(email); }
    finally {
      setBusy(false);
      setSent(true);
    }
  }

  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3>{en.auth.forgotTitle}</h3>
        <p className="lead">{en.auth.forgotLede}</p>
        {sent ? <Alert tone="success">{en.auth.forgotSent}</Alert> : (
          <Input label={en.auth.emailLabel} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        )}
        <div className="modal-actions">
          <Button variant="ghost" onClick={onClose}>{sent ? 'Fermer' : 'Annuler'}</Button>
          {!sent && <Button variant="primary" disabled={busy || !email} onClick={send}>{busy ? '…' : 'Envoyer le lien'}</Button>}
        </div>
      </div>
    </div>
  );
}
